/**
 * Routes and the session boot.
 *
 * The session is restored once, here, before any route renders content. A cold
 * load has no access token in memory by design, so `restore()` 401s, refreshes
 * from the httpOnly cookie and continues — which is why the shell shows a
 * restoring state rather than flashing the sign-in page at a signed-in user.
 *
 * Every guarded route names **both** its service and its permission, in that
 * order. The order matters and matches the server: a disabled service must not
 * present as a missing permission, or somebody goes off to edit a role that was
 * never the problem.
 */

import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  ChangePasswordPage,
  Guarded,
  RequireActivated,
  RequireAuth,
  SignInPage,
  useSession,
} from '@connect/web-shared';

import { AdminShell } from './admin-shell';
import { AuditLogPage } from '../features/audit/audit-log-page';
import { ClientDetailPage } from '../features/clients/client-detail-page';
import { ClientsPage } from '../features/clients/clients-page';
import { ContactsPage } from '../features/contacts/contacts-page';
import { ConnectCustomerPage } from '../features/customers/connect-customer-page';
import { CustomerDatabasePage } from '../features/customers/customer-database-page';
import { AllocationsPage } from '../features/projects/allocations-page';
import { EmployeesPage } from '../features/people/employees-page';
import { ExpensesPage } from '../features/finance/expenses-page';
import { InvoiceDetailPage } from '../features/finance/invoice-detail-page';
import { InvoicesPage } from '../features/finance/invoices-page';
import { OrgChartPage } from '../features/people/org-chart-page';
import { OrgUnitsPage } from '../features/people/org-units-page';
import { PaymentsPage } from '../features/finance/payments-page';
import { ProjectDetailPage } from '../features/projects/project-detail-page';
import { ProjectsPage } from '../features/projects/projects-page';
import { TasksPage } from '../features/tasks/tasks-page';
import { TicketDetailPage } from '../features/tickets/ticket-detail-page';
import { TicketsPage } from '../features/tickets/tickets-page';
import { TimeEntriesPage } from '../features/time/time-entries-page';
import { TimesheetsPage } from '../features/time/timesheets-page';
import { DashboardPage } from '../features/dashboard/dashboard-page';
import { NotFoundPage } from '../features/dashboard/not-found-page';
import { ProposalDetailPage } from '../features/proposals/proposal-detail-page';
import { ProposalsPage } from '../features/proposals/proposals-page';
import { ReportsPage } from '../features/reports/reports-page';
import { AiAutomationPage } from '../features/messaging/ai-automation-page';
import { WebhooksPage } from '../features/messaging/webhooks-page';
import { ConnectedAppsPage } from '../features/messaging/connected-apps-page';
import { ConnectedAppDetailPage } from '../features/messaging/connected-app-detail-page';
import { RcsPricingPage } from '../features/messaging/rcs-pricing-page';
import { BillingPage } from '../features/messaging/billing-page';
import { CreditPurchasesPage } from '../features/messaging/credit-purchases-page';
import { CallingPage } from '../features/messaging/calling-page';
import { EmailPricingPage } from '../features/messaging/email-pricing-page';
import { EmailReviewPage } from '../features/messaging/email-review';
import { MessagingHealthPage } from '../features/messaging/messaging-health-page';
import { ProviderAccountPage, ProvidersPage } from '../features/messaging/providers-page';
import { DocsPage } from '../features/docs/docs-page';
import { MessagingUsagePage } from '../features/messaging/messaging-usage-page';
import { RecordCodesPage } from '../features/record-codes/record-codes-page';
import { RolesPage } from '../features/access/roles-page';
import { ServicesPage } from '../features/services/services-page';
import { IndividualRequestPage } from '../features/individual-requests/request-page';
import { IndividualRequestsPage } from '../features/individual-requests/individual-requests-page';
import { ConsentRecordsPage } from '../features/legal/consent-records-page';
import { ConsentRequirementsPage } from '../features/legal/consent-requirements-page';
import { LegalDocumentPage } from '../features/legal/legal-document-page';
import { LegalDocumentsPage } from '../features/legal/legal-documents-page';
import { SecurityIncidentPage, SecurityIncidentsPage } from '../features/legal/security-incidents-page';
import { SettingsPage } from '../features/settings/settings-page';
import { PartnerCommissionsPage } from '../features/partners/commissions-page';
import { PartnerDetailPage } from '../features/partners/partner-detail-page';
import { PartnerPayoutsPage } from '../features/partners/payouts-page';
import { PartnerPoliciesPage } from '../features/partners/policies-page';
import { PartnerRegistrationsPage } from '../features/partners/registrations-page';
import { PartnersPage } from '../features/partners/partners-page';
import { PartnerTaxPage } from '../features/partners/tax-page';
import { PayoutChecksPage, PayoutReviewPage } from '../features/partners/payout-checks-page';
import { UsersPage } from '../features/access/users-page';

export function App() {
  const restore = useSession((state) => state.restore);
  const status = useSession((state) => state.status);

  useEffect(() => {
    // Only once. `status` is not in the dependency list on purpose: restoring
    // sets it, and depending on it would loop.
    if (status === 'unknown') void restore();
  }, [status, restore]);

  return (
    <Routes>
      <Route
        path="/sign-in"
        // Staff sign in with a password or a one-time code, by username, email
        // or sign-in mobile. The admin door's codes are staff codes only.
        element={<SignInPage subtitle="Employee portal" offersCodes />}
      />

      <Route
        path="/*"
        element={
          <RequireAuth>
            <Routes>
              {/* Outside RequireActivated: an account that must change its
                  password has to be able to reach the one page that lets it. */}
              <Route path="/change-password" element={<ChangePasswordPage />} />

              <Route
                path="/*"
                element={
                  <RequireActivated>
                    <AdminShell>
                      <Routes>
                        <Route path="/" element={<DashboardPage />} />

                        {/* ── crm ─────────────────────────── */}
                        <Route
                          path="/clients"
                          element={
                            <Guarded service="crm" permission="crm.client.view">
                              <ClientsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/clients/:publicId"
                          element={
                            <Guarded service="crm" permission="crm.client.view">
                              <ClientDetailPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/customers"
                          element={
                            <Guarded service="crm" permission="crm.customer.view">
                              <CustomerDatabasePage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/customers/:publicId"
                          element={
                            <Guarded service="crm" permission="crm.customer_identity.view">
                              <ConnectCustomerPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/contacts"
                          element={
                            <Guarded service="crm" permission="crm.contact.view">
                              <ContactsPage />
                            </Guarded>
                          }
                        />

                        <Route
                          path="/proposals"
                          element={
                            <Guarded service="crm" permission="crm.proposal.view">
                              <ProposalsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/proposals/:publicId"
                          element={
                            <Guarded service="crm" permission="crm.proposal.view">
                              <ProposalDetailPage />
                            </Guarded>
                          }
                        />

                        {/* ── projects ─────────────────────── */}
                        <Route
                          path="/projects"
                          element={
                            <Guarded service="projects" permission="projects.project.view">
                              <ProjectsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/projects/:publicId"
                          element={
                            <Guarded service="projects" permission="projects.project.view">
                              <ProjectDetailPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/tasks"
                          element={
                            <Guarded service="projects" permission="projects.task.view">
                              <TasksPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/allocations"
                          element={
                            <Guarded service="projects" permission="projects.allocation.view">
                              <AllocationsPage />
                            </Guarded>
                          }
                        />

                        {/* ── support ──────────────────────── */}
                        <Route
                          path="/tickets"
                          element={
                            <Guarded service="support" permission="support.ticket.view">
                              <TicketsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/tickets/:publicId"
                          element={
                            <Guarded service="support" permission="support.ticket.view">
                              <TicketDetailPage />
                            </Guarded>
                          }
                        />

                        {/* ── time ─────────────────────────── */}
                        <Route
                          path="/time-entries"
                          element={
                            <Guarded service="timesheets" permission="timesheets.entry.view">
                              <TimeEntriesPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/timesheets"
                          element={
                            <Guarded service="timesheets" permission="timesheets.sheet.view">
                              <TimesheetsPage />
                            </Guarded>
                          }
                        />

                        {/* ── finance ──────────────────────── */}
                        <Route
                          path="/invoices"
                          element={
                            <Guarded service="finance" permission="finance.invoice.view">
                              <InvoicesPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/invoices/:publicId"
                          element={
                            <Guarded service="finance" permission="finance.invoice.view">
                              <InvoiceDetailPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/payments"
                          element={
                            <Guarded service="finance" permission="finance.payment.view">
                              <PaymentsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/expenses"
                          element={
                            <Guarded service="finance" permission="finance.expense.view">
                              <ExpensesPage />
                            </Guarded>
                          }
                        />

                        {/* ── people ───────────────────────── */}
                        <Route
                          path="/employees"
                          element={
                            <Guarded service="hr" permission="hr.employee.view">
                              <EmployeesPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/departments"
                          element={
                            <Guarded service="hr" permission="hr.department.view">
                              <OrgUnitsPage />
                            </Guarded>
                          }
                        />
                        {/* Teams share the page with departments — they are two
                            tabs on one idea, so the route points at the same
                            component rather than duplicating it. */}
                        <Route
                          path="/teams"
                          element={
                            <Guarded service="hr" permission="hr.team.view">
                              <OrgUnitsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/org-chart"
                          element={
                            <Guarded service="hr" permission="hr.employee.view">
                              <OrgChartPage />
                            </Guarded>
                          }
                        />

                        {/* ── insight ──────────────────────── */}
                        <Route
                          path="/reports"
                          element={
                            <Guarded service="reports" permission="reports.report.view">
                              <ReportsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/usage"
                          element={
                            <Guarded service="messaging" permission="messaging.usage.view">
                              <MessagingUsagePage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/health"
                          element={
                            <Guarded service="messaging" permission="messaging.channel.view">
                              <MessagingHealthPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/ai"
                          element={
                            <Guarded service="messaging" permission="messaging.ai.view">
                              <AiAutomationPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/webhooks"
                          element={
                            <Guarded service="integrations" permission="messaging.webhook.view">
                              <WebhooksPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/connected-apps"
                          element={
                            <Guarded service="integrations" permission="messaging.connected_app.view">
                              <ConnectedAppsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/connected-apps/:publicId"
                          element={
                            <Guarded service="integrations" permission="messaging.connected_app.view">
                              <ConnectedAppDetailPage />
                            </Guarded>
                          }
                        />
                        {/* ── the partner programme (docs/30) ─ */}
                        <Route
                          path="/partners"
                          element={
                            <Guarded service="partners" permission="partners.partner.view">
                              <PartnersPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/partners/:publicId"
                          element={
                            <Guarded service="partners" permission="partners.partner.view">
                              <PartnerDetailPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/partners-customers"
                          element={
                            <Guarded service="partners" permission="partners.attribution.view">
                              <PartnerRegistrationsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/partners-policies"
                          element={
                            <Guarded service="partners" permission="partners.policy.view">
                              <PartnerPoliciesPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/partners-commissions"
                          element={
                            <Guarded service="partners" permission="partners.ledger.view">
                              <PartnerCommissionsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/partners-payouts"
                          element={
                            <Guarded service="partners" permission="partners.payout.view">
                              <PartnerPayoutsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/partners-tax"
                          element={
                            <Guarded service="partners" permission="partners.tax.view">
                              <PartnerTaxPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/partners-checks"
                          element={
                            <Guarded service="partners" permission="partners.verification.view">
                              <PayoutChecksPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/partners-checks/:id"
                          element={
                            <Guarded service="partners" permission="partners.verification.view">
                              <PayoutReviewPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/billing"
                          element={
                            <Guarded service="messaging" permission="messaging.billing.view">
                              <BillingPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/providers"
                          element={
                            <Guarded service="messaging" permission="messaging.provider_account.view">
                              <ProvidersPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/providers/:publicId"
                          element={
                            <Guarded service="messaging" permission="messaging.provider_account.view">
                              <ProviderAccountPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/credit-purchases"
                          element={
                            <Guarded service="messaging" permission="messaging.credit_purchase.view">
                              <CreditPurchasesPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/rcs"
                          element={
                            <Guarded service="messaging" permission="messaging.rcs.view">
                              <RcsPricingPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/calling"
                          element={
                            <Guarded service="messaging" permission="messaging.calling.view">
                              <CallingPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/email"
                          element={
                            <Guarded service="messaging" permission="messaging.email.view">
                              <EmailPricingPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/messaging/email/reviews/:publicId"
                          element={
                            <Guarded service="messaging" permission="messaging.email.view">
                              <EmailReviewPage />
                            </Guarded>
                          }
                        />

                        {/* ── access (core) ───────────────── */}
                        <Route
                          path="/access/users"
                          element={
                            <Guarded permission="iam.user.view">
                              <UsersPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/access/roles"
                          element={
                            <Guarded permission="iam.role.view">
                              <RolesPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/access/audit"
                          element={
                            <Guarded permission="audit.log.view">
                              <AuditLogPage />
                            </Guarded>
                          }
                        />

                        {/* ── administration (core) ───────── */}
                        <Route
                          path="/legal/documents"
                          element={
                            <Guarded permission="core.legal_document.view">
                              <LegalDocumentsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/legal/documents/:publicId"
                          element={
                            <Guarded permission="core.legal_document.view">
                              <LegalDocumentPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/legal/requirements"
                          element={
                            <Guarded permission="core.consent_requirement.view">
                              <ConsentRequirementsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/legal/consents"
                          element={
                            <Guarded permission="core.consent_record.view">
                              <ConsentRecordsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/legal/incidents"
                          element={
                            <Guarded permission="core.security_incident.view">
                              <SecurityIncidentsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/legal/incidents/:publicId"
                          element={
                            <Guarded permission="core.security_incident.view">
                              <SecurityIncidentPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/individual-requests"
                          element={
                            <Guarded service="messaging" permission="messaging.individual_request.view">
                              <IndividualRequestsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/individual-requests/:publicId"
                          element={
                            <Guarded service="messaging" permission="messaging.individual_request.view">
                              <IndividualRequestPage />
                            </Guarded>
                          }
                        />

                        <Route
                          path="/settings/services"
                          element={
                            <Guarded permission="core.service.view">
                              <ServicesPage />
                            </Guarded>
                          }
                        />
                        {/* The guides shipped with the product; each is gated on its subject. */}
                        <Route
                          path="/docs"
                          element={
                            <Guarded permission="core.doc.view">
                              <DocsPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/docs/:slug"
                          element={
                            <Guarded permission="core.doc.view">
                              <DocsPage />
                            </Guarded>
                          }
                        />
                        {/* How the organization's own records are numbered. Its own
                            permission, not `core.setting`: a code is on every document
                            that leaves the building. */}
                        <Route
                          path="/settings/record-codes"
                          element={
                            <Guarded permission="core.code_pattern.view">
                              <RecordCodesPage />
                            </Guarded>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <Guarded permission="core.setting.view">
                              <SettingsPage />
                            </Guarded>
                          }
                        />

                        <Route path="/sign-in" element={<Navigate to="/" replace />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </AdminShell>
                  </RequireActivated>
                }
              />
            </Routes>
          </RequireAuth>
        }
      />
    </Routes>
  );
}
